/**
 * Normalization and classification of the company-registry lookup input.
 * Kept separate from the calling hooks so the error branching stays unit-testable.
 */
import { validateCompanyId, type CompanyRegistryCountry } from '@insio/validation';

export type CompanyIdInputError = 'invalidFormat' | 'notFound';

export interface CompanyIdClassification {
  /** Whitespace and the country's own VAT prefix removed, ready for the lookup. */
  normalized: string;
  /** null when the value is a usable company ID. */
  error: CompanyIdInputError | null;
}

/**
 * Accepts a company registration number, or a VAT ID whose prefix matches the country
 * and whose remainder is the registration number (true for CZ, not for SK).
 *
 * A wrongly shaped value is a format problem. A correctly shaped value that fails the
 * country's checksum cannot belong to any company, so it is reported as not-found — the
 * same message a well-formed but unregistered number gets from the register.
 */
export function classifyCompanyIdInput(
  raw: string,
  countryCode: CompanyRegistryCountry
): CompanyIdClassification {
  const compact = raw.replace(/\s+/g, '');
  // The ISO country code doubles as the VAT prefix for every register we support.
  const normalized = compact.toUpperCase().startsWith(countryCode)
    ? compact.slice(countryCode.length)
    : compact;

  const { code } = validateCompanyId(normalized, countryCode);

  return { normalized, error: code === 'checksum' ? 'notFound' : code ? 'invalidFormat' : null };
}
